// @flow

import * as React from 'react';
import { createRoot } from 'react-dom/client';
import Honeybadger from '@honeybadger-io/js';

import { HoneybadgerErrorBoundary } from '@honeybadger-io/react';
import ErrorComponent from 'common/components/ErrorComponent.js';
import StrictModeInDevelopment from 'common/components/StrictModeInDevelopment';

type AnyProps = {| [string]: any |};

type ComponentMap = any;
type LazyComponentMap = any;

// react-dom/client ships no Flow libdef, so there's no real `Root` type to
// import -- treat roots as opaque values.
type Root = any;

const roots: WeakMap<HTMLElement, Root> = new WeakMap();

function getRoot(node: HTMLElement, identifierPrefix: string): Root {
  const existing = roots.get(node);
  if (existing) return existing;

  // The prefix is the mount's component id, so every id generated inside a root
  // is traceable to the mount that owns it. These do not reproduce the ids the
  // prerender emitted for the same mount — React marks server-generated ids
  // apart from client-generated ones — and they don't need to, because this is
  // createRoot rather than hydrateRoot: the client render replaces the
  // prerendered DOM, ids included.
  const root = createRoot(node, { identifierPrefix });
  roots.set(node, root);
  return root;
}

export function unmountComponent(node: HTMLElement) {
  const root = roots.get(node);
  if (!root) return;

  root.unmount();
  roots.delete(node);
  delete node.dataset.reactMounted;
}

// Marks the mount container once React has committed — the earliest moment
// the component's form fields and handlers actually exist. render() returning
// is not that moment, and for lazy components the chunk fetch hasn't even
// started then. The marker lives on the container node because neither
// createRoot nor hydrateRoot ever touches the container's own attributes,
// and it sits outside the error boundary so a component that crashes into
// ErrorComponent still marks its container — pre-mount submit guards read
// this attribute, and an unmarked mount would freeze its form's submit for
// good. The effect re-running (StrictMode) is harmless: same attribute, and
// the event is only informational.
function MountedMarker({
  node,
  children
}: {|
  node: HTMLElement,
  children: React.Node
|}): React.Node {
  React.useEffect(() => {
    node.dataset.reactMounted = 'true';
    node.dispatchEvent(
      new CustomEvent('venuu:react-mounted', { bubbles: true })
    );
  }, [node]);
  return children;
}

export function exposeComponents(mountableComponents: ComponentMap) {
  window.Venuu.REACT_COMPONENTS = {
    ...window.Venuu.REACT_COMPONENTS,
    ...mountableComponents
  };
}

export function exposeLazyComponents(mountableComponents: LazyComponentMap) {
  window.Venuu.REACT_LAZY_COMPONENTS = {
    ...window.Venuu.REACT_LAZY_COMPONENTS,
    ...mountableComponents
  };
}

export function createElement(
  component: React.ComponentType<AnyProps>,
  props: AnyProps
): React.Node {
  // $FlowFixMe[not-a-function] -- Suppressed during Flow upgrade to 0.241.0. Delete this comment to view the error.
  return React.createElement(component, props);
}

const mountComponentFromNode = (node: HTMLElement) => {
  const componentName = node.getAttribute('data-react-class');
  const componentId = node.getAttribute('data-component-id');
  if (!componentName || !componentId) {
    throw new Error("Can't mount react node");
  }
  const props = window.Venuu.REACT_PROPS[`${componentName}:${componentId}`];
  mountComponent(componentName, props, node, componentId);
};

function mountComponent(
  componentName: string,
  props: AnyProps,
  node: HTMLElement,
  componentId: string
) {
  const normalComponent =
    window.Venuu.REACT_COMPONENTS &&
    window.Venuu.REACT_COMPONENTS[componentName];
  const loadLazyComponent =
    window.Venuu.REACT_LAZY_COMPONENTS &&
    window.Venuu.REACT_LAZY_COMPONENTS[componentName];

  if (normalComponent) {
    render(normalComponent, props, node, componentId);
  } else if (loadLazyComponent) {
    loadLazyComponent()
      .then(component => {
        render(component, props, node, componentId);
      })
      .catch(err => {
        // eslint-disable-next-line no-console
        console.error(err);
      });
  } else {
    throw new Error(
      `Could not find mountable component ${componentName}. ` +
        'You need to expose it through REACT_COMPONENTS or REACT_LAZY_COMPONENTS.'
    );
  }
}

function render(
  component: any,
  props: AnyProps,
  node: HTMLElement,
  identifierPrefix: string
) {
  try {
    Honeybadger.setContext({ component: component.name, props });
    // $FlowFixMe[incompatible-type] createElement() has a too wide potential return type
    const element: React.Element<React.ElementType> = createElement(
      component,
      props
    );
    // TODO: Call hydrateRoot instead when the component has been server-rendered
    getRoot(node, identifierPrefix).render(
      <StrictModeInDevelopment>
        <MountedMarker node={node}>
          <HoneybadgerErrorBoundary
            honeybadger={Honeybadger}
            ErrorComponent={ErrorComponent}
          >
            {element}
          </HoneybadgerErrorBoundary>
        </MountedMarker>
      </StrictModeInDevelopment>
    );
  } finally {
    Honeybadger.setContext({ component: null, props: null });
  }
}

export function mountAllComponents() {
  const reactNodes = document.querySelectorAll('[data-react-class]');

  // YYyeah, document.querySelectorAll doesn't return an Array but a NodeList object. And that
  // fucker naturally doesn't support .map function, because DOM API sucks balls, so we have to
  // rely on normal for-loop to iterate the nodes.
  // https://developer.mozilla.org/en-US/docs/Web/API/NodeList#Why_is_NodeList_not_an_Array
  for (let i = 0; i < reactNodes.length; i++) {
    mountComponentFromNode(reactNodes[i]);
  }
}
